import { useEffect, useRef, useState } from "react";
import { Upload, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, imageUrl } from "../../lib/api";

export default function Settings() {
  const [logo, setLogo] = useState("");
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setLogo(r.data.logo || "");
      setStatistics(r.data.statistics || []);
    }).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(res.data.url);
      toast.success("Logo yüklendi. Kaydetmeyi unutmayın.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.put("/admin/settings", { logo, statistics });
      toast.success("Ayarlar kaydedildi. Ana sayfaya otomatik yansıyacaktır.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStat = (index, key, value) => {
    setStatistics((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  if (loading) return <p className="text-sm text-[#64748B]">Yükleniyor...</p>;

  return (
    <div data-testid="admin-settings-page" className="max-w-3xl">
      <h1 className="font-display font-700 text-xl text-white mb-1">Site Ayarları</h1>
      <p className="text-sm text-[#64748B] mb-8">Logo ve istatistik değerlerini yönetin</p>

      <div className="rounded-2xl border border-white/[0.06] bg-[#0A0E17] p-6 mb-6">
        <h2 className="text-sm font-semibold text-white mb-5">Site Logosu</h2>
        <div className="flex items-center gap-5">
          {logo ? (
            <img src={imageUrl(logo)} alt="Logo" className="w-16 h-16 rounded-xl object-cover" data-testid="settings-logo-preview" />
          ) : (
            <div className="w-16 h-16 rounded-xl border border-dashed border-white/15 flex items-center justify-center text-[#64748B]">
              <Upload size={18} />
            </div>
          )}
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="settings-logo-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="settings-logo-upload-button"
              className="text-xs font-semibold text-cyan-300 border border-cyan-400/30 rounded-full px-4 py-2 hover:bg-cyan-400/10 transition-colors disabled:opacity-50"
            >
              {uploading ? "Yükleniyor..." : logo ? "Logoyu Değiştir" : "Logo Yükle"}
            </button>
            {logo && (
              <button onClick={() => setLogo("")} data-testid="settings-logo-remove" className="ml-3 text-xs text-red-300 hover:text-red-200">
                Kaldır
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-[#0A0E17] p-6 mb-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-semibold text-white">İstatistikler</h2>
          <button
            onClick={() => setStatistics((prev) => [...prev, { value: "", label: "" }])}
            data-testid="settings-stat-add"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-100"
          >
            <Plus size={14} /> Ekle
          </button>
        </div>
        <div className="space-y-3" data-testid="settings-statistics-list">
          {statistics.map((stat, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`settings-stat-row-${i}`}>
              <input
                data-testid={`settings-stat-value-${i}`}
                className={`${inputCls} w-32`}
                placeholder="Değer (örn: 100+)"
                value={stat.value}
                onChange={(e) => updateStat(i, "value", e.target.value)}
              />
              <input
                data-testid={`settings-stat-label-${i}`}
                className={`${inputCls} flex-1`}
                placeholder="Etiket (örn: Yayıncı)"
                value={stat.label}
                onChange={(e) => updateStat(i, "label", e.target.value)}
              />
              <button
                onClick={() => setStatistics((prev) => prev.filter((_, idx) => idx !== i))}
                data-testid={`settings-stat-delete-${i}`}
                aria-label="İstatistiği sil"
                className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/40 transition-colors shrink-0"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save-button" className="btn-glow text-sm font-semibold text-white px-8 py-3.5 rounded-xl disabled:opacity-50">
        {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
      </button>
    </div>
  );
}
